"use client";

import * as React from "react";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { useGlobalDrawerStack } from "@wtc/ui";
import { addRecent } from "./recents";
import { useMultiSelectState } from "./useMultiSelectState";
import { ResponsiveCommandMultiSelectTrigger } from "./ResponsiveCommandMultiSelectTrigger";
import { ResponsiveCommandMultiSelectDesktop } from "./ResponsiveCommandMultiSelectDesktop";
import { ResponsiveCommandMultiSelectMobile } from "./ResponsiveCommandMultiSelectMobile";
import {
  ResponsiveCommandMultiSelectInlineFloating,
  ResponsiveCommandMultiSelectInlineReplace,
} from "./ResponsiveCommandMultiSelectInline";
import { useFloatingDropdown } from "./useFloatingDropdown";
import type { SelectOption } from "./types";

interface ResponsiveCommandMultiSelectProps {
  value: string[];
  onValueChange: (values: string[], options?: SelectOption[]) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
  isSearching?: boolean;
  className?: string;
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  highZIndex?: boolean;
  // Recents (optional)
  enableRecents?: boolean;
  recentKey?: string; // e.g., "contacts"
  recentMax?: number; // default 8
  onRecordRecent?: (item: { id: string; label: string }) => void;
  // Layout
  forceDrawer?: boolean; // force drawer on desktop for full-width UX
  // Controlled open state
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  autoOpen?: boolean; // auto-open on mount
  // Infinite scrolling (optional)
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /** When true, expand inline (search + list below) instead of popover. Use in modals. */
  expandInline?: boolean;
  /** When expandInline is true: "floating" = trigger stays, dropdown floats below (default); "replace" = trigger replaced by panel */
  expandInlineVariant?: "replace" | "floating";
}

export function ResponsiveCommandMultiSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  disabled = false,
  searchValue,
  onSearchChange,
  isSearching = false,
  className,
  title = "Select Options",
  description,
  searchPlaceholder = "Search options...",
  highZIndex = false,
  enableRecents = false,
  recentKey,
  recentMax = 8,
  onRecordRecent,
  forceDrawer = false,
  open: controlledOpen,
  onOpenChange,
  autoOpen = false,
  onLoadMore,
  hasMore,
  isLoadingMore,
  expandInline = false,
  expandInlineVariant = "floating",
}: ResponsiveCommandMultiSelectProps) {
  const [internalOpen, setInternalOpen] = React.useState(autoOpen);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null) as React.RefObject<HTMLButtonElement>;
  const containerRef = React.useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const dropdownRef = React.useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);
  const [internalSearch, setInternalSearch] = React.useState("");
  const [recentsNonce, setRecentsNonce] = React.useState(0);

  // Use controlled open state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  // Use internal search state when no onSearchChange handler is provided
  const effectiveSearch = onSearchChange ? searchValue || "" : internalSearch;

  // Keep focus on input when open
  React.useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, searchValue]);

  // Measure trigger width on open (desktop) to prevent content jumping
  React.useEffect(() => {
    if (isDesktop && open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerWidth(rect.width);
    }
  }, [isDesktop, open]);

  // Use multi-select state hook
  const { selectedOptions } = useMultiSelectState({ value, options });

  const toggleSelect = React.useCallback(
    (id: string) => {
      const isSelected = value.includes(id);
      const nextValues = isSelected ? value.filter((v) => v !== id) : [...value, id];
      const selected = options.filter((o) => nextValues.includes(o.value));
      onValueChange(nextValues, selected);
      // Record recent only when adding (not removing)
      if (!isSelected && enableRecents && recentKey) {
        const opt = options.find((o) => o.value === id);
        if (opt) {
          const record = { id, label: opt.label };
          try {
            if (onRecordRecent) onRecordRecent(record);
          else addRecent(recentKey, record, recentMax);
          } catch {}
          setRecentsNonce((n) => n + 1);
        }
      }
    },
    [value, options, onValueChange]
  );

  const clearAll = React.useCallback(() => {
    onValueChange([]);
    onSearchChange?.("");
  }, [onValueChange, onSearchChange]);

  const handleClearRecents = React.useCallback(() => {
    setRecentsNonce((n) => n + 1);
  }, []);

  const trigger = (
    <ResponsiveCommandMultiSelectTrigger
      selectedOptions={selectedOptions}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      triggerRef={triggerRef}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
        if (!isDesktop || forceDrawer) {
          openDrawer("mobile-command-multi-select");
        }
      }}
    />
  );

  const listProps = {
    inputRef,
    searchPlaceholder,
    effectiveSearch,
    onSearchChange: onSearchChange ?? setInternalSearch,
    isSearching,
    selectedOptions,
    value,
    onToggleSelect: toggleSelect,
    onClearAll: clearAll,
    enableRecents,
    recentKey,
    recentsNonce,
    options,
    onLoadMore,
    hasMore,
    isLoadingMore,
    onClearRecents: handleClearRecents,
  };

  // Always call hooks at the top level - no conditional hook calls
  const { openDrawer } = useGlobalDrawerStack();

  React.useEffect(() => {
    if (autoOpen && !open && (!isDesktop || forceDrawer)) {
      setOpen(true);
      openDrawer("mobile-command-multi-select");
    }
  }, [autoOpen, open, isDesktop, forceDrawer, openDrawer]);

  // Floating dropdown side-effects (positioning, outside click, escape)
  const dropdownPosition = useFloatingDropdown({
    enabled: expandInline && expandInlineVariant === "floating",
    open,
    triggerRef,
    containerRef,
    dropdownRef,
    setOpen,
  });

  // Inline expansion - floating variant: trigger stays, dropdown floats below (e.g. tickets/create page)
  if (expandInline && expandInlineVariant === "floating") {
    return (
      <ResponsiveCommandMultiSelectInlineFloating
        open={open}
        setOpen={setOpen}
        containerRef={containerRef}
        dropdownRef={dropdownRef}
        triggerRef={triggerRef}
        dropdownPosition={dropdownPosition}
        selectedOptions={selectedOptions}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        title={title}
        listProps={listProps}
      />
    );
  }

  // Inline expansion - replace variant: when open, show search + list below. Use in modals to avoid popover positioning.
  if (expandInline) {
    return (
      <ResponsiveCommandMultiSelectInlineReplace
        open={open}
        setOpen={setOpen}
        triggerRef={triggerRef}
        selectedOptions={selectedOptions}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        listProps={listProps}
      />
    );
  }

  // Use conditional rendering instead of conditional returns
  if (isDesktop && !forceDrawer) {
    return (
      <ResponsiveCommandMultiSelectDesktop
        open={open}
        onOpenChange={setOpen}
        triggerWidth={triggerWidth}
        title={title}
        highZIndex={highZIndex}
        trigger={trigger}
        listProps={listProps}
      />
    );
  }

  // Mobile: Render trigger button + drawer
  return (
    <ResponsiveCommandMultiSelectMobile
      drawerId="mobile-command-multi-select"
      title={title}
      description={description}
      trigger={trigger}
      listProps={listProps}
    />
  );
}
